<div class="auth-settings-page">
  @if (configResource.isLoading()) {
    <zag-loading-state [message]="'common.loading' | translate" />
  } @else if (configResource.error()) {
    <zag-error-state [message]="'settings.auth.loadError' | translate" backLink="/settings" [backText]="'common.back' | translate" />
  } @else {
    <ng-template zagPageActions>
      <button zagButton type="button" [disabled]="isSaving() || settingsForm.invalid" (click)="onSave()">
        {{ 'common.save' | translate }}
      </button>
    </ng-template>

    <zag-entity-detail-layout>
      <div slot="sidebar">
        <zag-profile-summary-card [avatarName]="profileName()" avatarIcon="lock" [subtitle]="profileSubtitle()" [stats]="profileStats()" />
      </div>

      <form slot="content" [formGroup]="settingsForm">
        <zag-accordion>
          <!-- Email & Password -->
          <zag-accordion-item [title]="'settings.auth.emailPassword.title' | translate" icon="mail_lock" [expanded]="true">
            <!-- Inline enable toggle in accordion header -->
            <zag-switch
              slot="actions"
              [checked]="!!settingsForm.get('emailAndPasswordEnabled')?.value"
              [ariaLabel]="'settings.auth.emailPassword.enable' | translate"
              (checkedChange)="settingsForm.patchValue({ emailAndPasswordEnabled: $event })"
            />
            @if (settingsForm.get('emailAndPasswordEnabled')?.value) {
              <div class="auth-settings-page__section">
                <div class="auth-settings-page__setting-row">
                  <span class="auth-settings-page__setting-row-label">
                    {{ 'settings.auth.emailPassword.usernameEnabled' | translate }}
                  </span>
                  <zag-switch
                    [checked]="!!settingsForm.get('usernameEnabled')?.value"
                    [ariaLabel]="'settings.auth.emailPassword.usernameEnabled' | translate"
                    (checkedChange)="settingsForm.patchValue({ usernameEnabled: $event })"
                  />
                </div>

                <div class="auth-settings-page__setting-row">
                  <span class="auth-settings-page__setting-row-label">
                    {{ 'settings.auth.emailPassword.requireVerification' | translate }}
                  </span>
                  <zag-switch
                    [checked]="!!settingsForm.get('requireEmailVerification')?.value"
                    [ariaLabel]="'settings.auth.emailPassword.requireVerification' | translate"
                    (checkedChange)="settingsForm.patchValue({ requireEmailVerification: $event })"
                  />
                </div>
                <div class="auth-settings-page__setting-row">
                  <span class="auth-settings-page__setting-row-label">
                    {{ 'settings.auth.emailPassword.minPasswordLength' | translate }}
                  </span>
                  <zag-number-input formControlName="minPasswordLength" [min]="6" [max]="128" [showControls]="false" />
                </div>
                <div class="auth-settings-page__setting-row">
                  <span class="auth-settings-page__setting-row-label">
                    {{ 'settings.auth.emailPassword.maxPasswordLength' | translate }}
                  </span>
                  <zag-number-input formControlName="maxPasswordLength" [min]="8" [max]="256" [showControls]="false" />
                </div>
              </div>
            }
          </zag-accordion-item>

          <!-- Microsoft -->
          <zag-accordion-item
            [title]="'settings.auth.socialProviders.microsoft' | translate"
            [statusIcon]="microsoftStatusIcon()"
            [statusVariant]="microsoftStatus() === 'partial' ? 'warning' : 'success'"
            [expanded]="false"
          >
            <svg
              slot="icon"
              class="auth-settings-page__provider-logo"
              viewBox="0 0 21 21"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <rect x="1" y="1" width="9" height="9" fill="#f25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
              <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
            </svg>
            <button
              slot="actions"
              type="button"
              class="auth-settings-page__help-btn"
              [zagTooltip]="'helpDialog.openTooltip.microsoft' | translate"
              [attr.aria-label]="'helpDialog.openTooltip.microsoft' | translate"
              (click)="openProviderHelp('microsoft')"
            >
              <span zagIcon="help-outline"></span>
            </button>
            <div class="auth-settings-page__section">
              <zag-form-field [label]="'settings.auth.socialProviders.tenantId' | translate" [required]="microsoftStatus() !== 'empty'">
                <zag-input formControlName="microsoftTenantId" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
              </zag-form-field>
              <zag-form-field [label]="'settings.auth.socialProviders.clientId' | translate" [required]="microsoftStatus() !== 'empty'">
                <zag-input formControlName="microsoftClientId" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
              </zag-form-field>
              <zag-form-field [label]="'settings.auth.socialProviders.clientSecret' | translate" [required]="microsoftStatus() !== 'empty'">
                <zag-input
                  formControlName="microsoftClientSecret"
                  type="password"
                  [placeholder]="secretPlaceholder(savedSecrets.microsoft)"
                />
              </zag-form-field>
            </div>
          </zag-accordion-item>

          <!-- Google -->
          <zag-accordion-item
            [title]="'settings.auth.socialProviders.google' | translate"
            [statusIcon]="googleStatusIcon()"
            [statusVariant]="googleStatus() === 'partial' ? 'warning' : 'success'"
            [expanded]="false"
          >
            <svg
              slot="icon"
              class="auth-settings-page__provider-logo"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            <button
              slot="actions"
              type="button"
              class="auth-settings-page__help-btn"
              [zagTooltip]="'helpDialog.openTooltip.google' | translate"
              [attr.aria-label]="'helpDialog.openTooltip.google' | translate"
              (click)="openProviderHelp('google')"
            >
              <span zagIcon="help-outline"></span>
            </button>
            <div class="auth-settings-page__section">
              <zag-form-field [label]="'settings.auth.socialProviders.clientId' | translate" [required]="googleStatus() !== 'empty'">
                <zag-input formControlName="googleClientId" placeholder="Client ID" />
              </zag-form-field>
              <zag-form-field [label]="'settings.auth.socialProviders.clientSecret' | translate" [required]="googleStatus() !== 'empty'">
                <zag-input formControlName="googleClientSecret" type="password" [placeholder]="secretPlaceholder(savedSecrets.google)" />
              </zag-form-field>
            </div>
          </zag-accordion-item>

          <!-- Apple -->
          <zag-accordion-item
            [title]="'settings.auth.socialProviders.apple' | translate"
            [statusIcon]="appleStatusIcon()"
            [statusVariant]="appleStatus() === 'partial' ? 'warning' : 'success'"
            [expanded]="false"
          >
            <svg
              slot="icon"
              class="auth-settings-page__provider-logo"
              viewBox="0 0 814 1000"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-37.5-167.2-103.1c-71.2-74.4-130.3-192.7-130.3-304.1 0-185.5 121.8-283.7 241.4-283.7 63.5 0 116.6 41.8 155.9 41.8 37.5 0 96.5-44.5 168.5-44.5 27.1 0 108.2 2.6 168.5 98.3zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z"
                fill="currentColor"
              />
            </svg>
            <button
              slot="actions"
              type="button"
              class="auth-settings-page__help-btn"
              [zagTooltip]="'helpDialog.openTooltip.apple' | translate"
              [attr.aria-label]="'helpDialog.openTooltip.apple' | translate"
              (click)="openProviderHelp('apple')"
            >
              <span zagIcon="help-outline"></span>
            </button>
            <div class="auth-settings-page__section">
              <zag-form-field [label]="'settings.auth.socialProviders.clientId' | translate" [required]="appleStatus() !== 'empty'">
                <zag-input formControlName="appleClientId" placeholder="com.example.app" />
              </zag-form-field>
              <zag-form-field [label]="'settings.auth.socialProviders.clientSecret' | translate" [required]="appleStatus() !== 'empty'">
                <zag-input formControlName="appleClientSecret" type="password" [placeholder]="secretPlaceholder(savedSecrets.apple)" />
              </zag-form-field>
            </div>
          </zag-accordion-item>

          <!-- LinkedIn -->
          <zag-accordion-item
            [title]="'settings.auth.socialProviders.linkedin' | translate"
            [statusIcon]="linkedinStatusIcon()"
            [statusVariant]="linkedinStatus() === 'partial' ? 'warning' : 'success'"
            [expanded]="false"
          >
            <svg
              slot="icon"
              class="auth-settings-page__provider-logo"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
                fill="#0A66C2"
              />
            </svg>
            <button
              slot="actions"
              type="button"
              class="auth-settings-page__help-btn"
              [zagTooltip]="'helpDialog.openTooltip.linkedin' | translate"
              [attr.aria-label]="'helpDialog.openTooltip.linkedin' | translate"
              (click)="openProviderHelp('linkedin')"
            >
              <span zagIcon="help-outline"></span>
            </button>
            <div class="auth-settings-page__section">
              <zag-form-field [label]="'settings.auth.socialProviders.clientId' | translate" [required]="linkedinStatus() !== 'empty'">
                <zag-input formControlName="linkedinClientId" placeholder="Client ID" />
              </zag-form-field>
              <zag-form-field [label]="'settings.auth.socialProviders.clientSecret' | translate" [required]="linkedinStatus() !== 'empty'">
                <zag-input
                  formControlName="linkedinClientSecret"
                  type="password"
                  [placeholder]="secretPlaceholder(savedSecrets.linkedin)"
                />
              </zag-form-field>
            </div>
          </zag-accordion-item>
        </zag-accordion>
      </form>
    </zag-entity-detail-layout>
  }
</div>
